import PwaUpdateDialog from './components/PwaUpdateDialog';
import { Button } from '@/components/ui/button';
import { isRecoveryRoute } from './isRecoveryRoute';
import { useBlockingInterface } from '@/features/Onboarding';
import { useEffect, useState } from 'react';
import { usePullToRefresh } from './usePullToRefresh';
import { usePwaUpdateContext } from './usePwaUpdateContext';

const PwaUpdateLayer = () => {
  const [hasRecoveryResourceError, setHasRecoveryResourceError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    activateUpdate,
    checkForUpdate,
    error,
    isReloadDeferred,
    isUpdateAvailable
  } = usePwaUpdateContext();

  const shouldShowUpdate = isUpdateAvailable && !isReloadDeferred;

  useBlockingInterface('pwa-update', shouldShowUpdate);

  usePullToRefresh({
    isEnabled: !isReloadDeferred && !isUpdateAvailable && !isUpdating,
    onRefresh: checkForUpdate
  });

  useEffect(() => {
    if (!isReloadDeferred) setHasRecoveryResourceError(false);
  }, [isReloadDeferred]);

  useEffect(() => {
    const onPreloadError = (event: Event): void => {
      if (isRecoveryRoute(window.location.pathname)) {
        event.preventDefault();
        setHasRecoveryResourceError(true);
        return;
      }
      const lastReload = sessionStorage.getItem('__preload_reload_ts');
      if (lastReload && Date.now() - Number(lastReload) < 30_000) return;
      sessionStorage.setItem('__preload_reload_ts', String(Date.now()));
      event.preventDefault();
      window.location.reload();
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
  }, []);

  const onUpdateApplication = async (): Promise<void> => {
    setIsUpdating(true);

    await activateUpdate().catch(() => undefined);
    setIsUpdating(false);
  };

  return (
    <>
      {hasRecoveryResourceError && isReloadDeferred && (
        <div
          className="fixed inset-x-4 bottom-4 z-[100] mx-auto grid max-h-[calc(100dvh-32px)] max-w-[390px] gap-3 overflow-y-auto p-4 text-sm rounded-2xl border border-border-strong bg-surface shadow-lg"
          role="alert"
        >
          <p className="font-semibold">Не удалось загрузить часть страницы</p>
          <p>
            Попробуйте продолжить без перезагрузки страницы.
            {' '}Если не получается, откройте ссылку из письма ещё раз,
            чтобы проверить восстановление. Если письма нет, запросите новую ссылку.
          </p>
          <Button onClick={() => setHasRecoveryResourceError(false)} type="button">Понятно</Button>
        </div>
      )}
      <PwaUpdateDialog
        error={error}
        isOpen={shouldShowUpdate}
        isUpdating={isUpdating}
        onUpdate={onUpdateApplication}
      />
    </>
  );
};

export default PwaUpdateLayer;